import DOMPurify from "dompurify";
import getDynamicImageUrl from "shared/General/components/DynamicImage/getDynamicImageUrl";
import {ContentType, Post} from "../../../widgets/modules/Content/types";

export function getContentType(rawContent: Post): ContentType {
    if (rawContent?.videoContent?.id) {
        return ContentType.SHORT_VIDEO;
    } else if (rawContent?.youtubeVideo) {
        return ContentType.YOUTUBE_VIDEO;
    }
    return ContentType.BLOG;
}

/**
 * Given a HTML string, replace all img source (which are just file names) with the
 * properly formatted S3 URLs
 * @param {HTMLDivElement} htmlDivElement - The HTML div element containing hyperlinks to be underlined.
 * @returns {string} htmlString with img source reformatted
 */
const hydrateImageSource = (htmlDivElement: HTMLDivElement): string => {
    const nodes = htmlDivElement.querySelectorAll("img");

    nodes.forEach((img) => {
        const source = img.getAttribute("src");
        if (source && !source.startsWith("blob") && !source.startsWith("http")) {
            const fileName = source.split("/").pop();
            img.src = getDynamicImageUrl(fileName);
        }
    });
    return htmlDivElement.innerHTML;
};

/**
 * Adds underline to all hyperlink elements within the given HTML div element.
 *
 * @param {HTMLDivElement} htmlDivElement - The HTML div element containing hyperlinks to be underlined.
 * @returns {string} The inner HTML of the modified div element.
 */
const addUnderLineLinks = (htmlDivElement: HTMLDivElement): string => {
    const hyperLinksHtmlTag = htmlDivElement.querySelectorAll("a");
    hyperLinksHtmlTag.forEach((a) => {
        a.style.textDecoration = "underline";
    });
    return htmlDivElement.innerHTML;
};

const sanitizeHTML = (htmlString: string): string => {
    return DOMPurify.sanitize(htmlString, {
        // Specify what scheme are permitted in URIs
        ALLOWED_URI_REGEXP:
            /^(?:(?:(?:f|ht)tps?|mailto|tel|blob):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
        // extend the existing array of allowed tags and their attributes
        ADD_TAGS: ["iframe"],
        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "target"],
    });
};

export const processHtmlContent = (htmlString: string): string => {
    if (htmlString == null) return null;

    const div = document.createElement("div");
    div.innerHTML = sanitizeHTML(htmlString);

    hydrateImageSource(div);
    addUnderLineLinks(div);

    return div.innerHTML;
};
